import { ChevronRightIcon, Text } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import styles from './SectionItem.module.scss';

interface SectionItemData {
  label: string;
  rate: number;
  progressColor: string;
}

interface SectionItemProps {
  description: string;
  totalRate: number;
  data: SectionItemData[];
  onClickNavigate?: () => void;
}

const buildGradient = (data: SectionItemData[]) => {
  const rateSum = data.reduce((sum, { rate }) => sum + rate, 0);

  if (rateSum === 0) {
    return 'transparent';
  }

  const { stops } = data.reduce<{ stops: string[]; offset: number }>(
    (acc, { rate, progressColor }) => {
      const start = (acc.offset / rateSum) * 100;
      const end = ((acc.offset + rate) / rateSum) * 100;
      acc.stops.push(`${progressColor} ${start}%`, `${progressColor} ${end}%`);
      acc.offset += rate;
      return acc;
    },
    { stops: [], offset: 0 }
  );

  return `linear-gradient(to right, ${stops.join(', ')})`;
};

const SectionItem = ({ description, totalRate, data, onClickNavigate }: SectionItemProps) => {
  const hasLegend = data.some(({ label }) => label);

  return (
    <div className={clsx(styles.item, { [styles['item--with-legend']]: hasLegend })}>
      <Text size="large" weight="semi-bold">
        {description}
      </Text>
      <div className={styles.chart}>
        <div className={styles['chart-background']} />
        <div className={styles['chart-progress']} style={{ width: `${totalRate}%`, background: buildGradient(data) }} />
        {hasLegend && (
          <div className={styles.legend}>
            {data
              .filter(({ rate }) => rate > 0)
              .map(({ label, rate, progressColor }) => (
                <div key={`${label}-${rate}`} className={styles['legend-item']}>
                  <span className={styles['legend-color']} style={{ background: progressColor }} />
                  <Text size="large" weight="regular">
                    {label}: {rate}%
                  </Text>
                </div>
              ))}
          </div>
        )}
      </div>
      <div className={styles.rate}>
        <Text size="large" weight="bold">
          {totalRate}%
        </Text>
        {onClickNavigate && <ChevronRightIcon size={16} onClick={onClickNavigate} style={{ cursor: 'pointer' }} />}
      </div>
    </div>
  );
};

export { SectionItem };
